// LOT 2.6 — Journal des contresignatures EC.
//
// Historique immuable append-only. La levée d'une contresignature ("unverify")
// n'efface pas l'entrée précédente, elle en ajoute une nouvelle avec sa raison.
// Filtres : num, actor, action.
import { useEffect, useState } from "react";
import { CheckCircle, XCircle, RotateCcw, Filter } from "lucide-react";
import { fetchJournal, unverifyAccount, clearAdminToken } from "@/lib/adminClient";

const ActionBadge = ({ action }) => {
  const cfg =
    action === "verify"
      ? { icon: <CheckCircle size={12} />, bg: "bg-emerald-100 text-emerald-900 border-emerald-300", label: "contresigné" }
      : { icon: <XCircle size={12} />, bg: "bg-amber-100 text-amber-900 border-amber-400", label: "levée" };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase px-1.5 py-0.5 rounded border ${cfg.bg}`}>
      {cfg.icon} {cfg.label}
    </span>
  );
};

/** Bouton "Lever la contresignature" avec double confirm + reason obligatoire. */
const UnverifyButton = ({ entry, onDone, onError }) => {
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    // 1er confirm — intention
    const first = window.confirm(
      `Lever la contresignature de ${entry.num} — ${entry.lib} ?\n\n` +
        `Rappel : la levée est TRACÉE dans le journal, pas un retour à zéro. ` +
        `L'historique reste préservé, mais la fiche redevient à relire.`
    );
    if (!first) return;

    // 2e étape — raison obligatoire
    const reason = window.prompt(
      "Raison de la levée (min 5 caractères, sera journalisée) :",
      ""
    );
    if (reason === null) return;
    if (reason.trim().length < 5) {
      alert("Raison trop courte (min 5 caractères).");
      return;
    }

    setBusy(true);
    try {
      await unverifyAccount(entry.num, "K.T.", reason.trim());
      onDone(entry.num);
    } catch (err) {
      if (err.message === "UNAUTHORIZED") {
        clearAdminToken();
        onError();
      } else {
        alert(`Erreur : ${err.message}`);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      data-testid={`unverify-btn-${entry.num}`}
      onClick={handleClick}
      disabled={busy}
      className="inline-flex items-center gap-1 text-xs bg-amber-100 hover:bg-amber-200 text-amber-900 font-semibold px-2 py-1 rounded border border-amber-300 disabled:opacity-50"
    >
      <RotateCcw size={12} /> {busy ? "…" : "Lever"}
    </button>
  );
};

export default function AdminJournal({ onLogout }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filterNum, setFilterNum] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const reload = async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await fetchJournal({
        num: filterNum || undefined,
        action: filterAction || undefined,
        limit: 200,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      if (e.message === "UNAUTHORIZED") {
        clearAdminToken();
        onLogout();
        return;
      }
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  // Sur unverify réussi, on recharge (l'entrée verify d'avant reste, une nouvelle entrée unverify apparaît en tête).
  const onUnverified = () => reload();

  const fmt = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  };

  return (
    <div data-testid="admin-journal-root" className="p-4 md:p-6 max-w-6xl mx-auto">
      <div className="mb-4">
        <h2 className="text-xl font-extrabold text-[#15294F] mb-1">Journal des contresignatures</h2>
        <p className="text-sm text-slate-600">
          Historique immuable — chaque contresignature et chaque levée est enregistrée.
          Total : <strong>{total}</strong> entrée{total > 1 ? "s" : ""}.
        </p>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-3 items-center mb-4 bg-white border border-slate-200 rounded-md p-3">
        <Filter size={14} className="text-slate-500" />
        <input
          data-testid="journal-filter-num"
          type="text"
          value={filterNum}
          onChange={(e) => setFilterNum(e.target.value)}
          placeholder="Filtrer par num (ex : 1052)"
          className="px-2 py-1 border border-slate-300 rounded text-sm font-mono w-40"
        />
        <select
          data-testid="journal-filter-action"
          value={filterAction}
          onChange={(e) => setFilterAction(e.target.value)}
          className="px-2 py-1 border border-slate-300 rounded text-sm"
        >
          <option value="">Toutes actions</option>
          <option value="verify">Contresignatures uniquement</option>
          <option value="unverify">Levées uniquement</option>
        </select>
        <button
          data-testid="journal-filter-apply"
          onClick={reload}
          className="text-sm bg-[#15294F] hover:bg-[#0f1f3d] text-white font-semibold px-3 py-1 rounded"
        >
          Appliquer
        </button>
        <button
          data-testid="journal-filter-reset"
          onClick={() => {
            setFilterNum("");
            setFilterAction("");
            setTimeout(reload, 0);
          }}
          className="text-sm bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 px-3 py-1 rounded"
        >
          Effacer
        </button>
      </div>

      {loading && <div className="text-slate-500 text-sm p-4">Chargement…</div>}
      {err && <div className="text-red-600 text-sm p-4">{err}</div>}

      {!loading && !err && items.length === 0 && (
        <div data-testid="journal-empty" className="text-slate-500 text-sm p-6 bg-white border border-slate-200 rounded text-center">
          Journal vide sur ce filtre — aucune contresignature enregistrée.
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {items.map((entry, idx) => (
          <div
            key={`${entry.num}-${entry.at}-${idx}`}
            data-testid={`journal-entry-${entry.num}-${idx}`}
            className="flex items-start gap-3 p-3 border-b last:border-b-0 border-slate-100 hover:bg-slate-50"
          >
            <div className="w-28 shrink-0 text-xs text-slate-500 font-mono">{fmt(entry.at)}</div>
            <div className="w-24 shrink-0">
              <ActionBadge action={entry.action} />
            </div>
            <div className="w-20 shrink-0 font-mono font-bold text-[#15294F]">{entry.num}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-900 truncate">{entry.lib}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                par <strong className="font-semibold">{entry.actor}</strong>
                {entry.audit_gravite && (
                  <span className={`ml-2 text-[10px] font-black px-1 py-0.5 rounded ${entry.audit_gravite === "P0" ? "bg-red-100 text-red-900" : "bg-orange-100 text-orange-900"}`}>
                    {entry.audit_gravite}
                  </span>
                )}
                {entry.reason && <span className="ml-2 italic">— « {entry.reason} »</span>}
              </div>
            </div>
            <div className="shrink-0">
              {entry.action === "verify" && (
                <UnverifyButton entry={entry} onDone={onUnverified} onError={onLogout} />
              )}
            </div>
          </div>
        ))}
      </div>

      <p className="mt-3 text-xs text-slate-500">
        Rappel : la levée d&apos;une contresignature n&apos;efface aucune entrée existante — elle ajoute une nouvelle entrée <em>&laquo;&nbsp;levée&nbsp;&raquo;</em> avec sa raison, préservant l&apos;historique complet.
      </p>
    </div>
  );
}
